import { Component, OnInit, ChangeDetectionStrategy, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ReminderType, ReminderPriority, ReminderFormData, Reminder, REMINDER_LEAD_TIME_OPTIONS } from '../../models/reminder.model';
import { RemindersService } from '../../services/reminders.service';
import { BackendApiService } from 'src/app/services/backend-api.service';

interface TypeOption {
  value: ReminderType;
  label: string;
  icon: string;
}

interface PriorityOption {
  value: ReminderPriority;
  label: string;
}

@Component({
    selector: 'app-add-reminder-modal',
    templateUrl: './add-reminder-modal.component.html',
    styleUrls: ['./add-reminder-modal.component.scss'],
    standalone: false
})
export class AddReminderModalComponent implements OnInit {
  reminderForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  isEditMode = false;
  editingReminderId?: string | number;

  locationList: any[] = [];
  selectedLocationsFromDropdown: any[] = [];

  @ViewChild('locationMenuTrigger') locationMenuTrigger!: MatMenuTrigger;

  reminderTypes: TypeOption[] = [
    { value: 'alert', label: 'Alert', icon: 'warning' },
    { value: 'meeting', label: 'Meeting', icon: 'group' },
    { value: 'event', label: 'Event', icon: 'event' },
    { value: 'deadline', label: 'Deadline', icon: 'error' }
  ];

  priorities: PriorityOption[] = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  leadTimeOptions = REMINDER_LEAD_TIME_OPTIONS;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddReminderModalComponent>,
    private remindersService: RemindersService,
    private backendApiService: BackendApiService,
    @Inject(MAT_DIALOG_DATA) public data?: {
      reminder?: Reminder;
      reminderable_type?: string;
      reminderable_id?: number;
      entityName?: string;  // Task/Ticket name
      location?: string;    // Task/Ticket location
      title?: string;       // Legacy support
    }
  ) {
    // Check if we're in edit mode
    if (data?.reminder) {
      this.isEditMode = true;
      this.editingReminderId = data.reminder.id;
    }
  }

  ngOnInit(): void {
    this.reminderForm = this.fb.group({
      title: ['', Validators.required],
      type: ['alert', Validators.required],
      date: ['', Validators.required],
      time: ['', Validators.required],
      priority: ['medium'],
      alert_before: [15],
      location: [''],
      description: ['']
    });

    // Pre-fill form if in edit mode
    if (this.isEditMode && this.data?.reminder) {
      const reminder = this.data.reminder;

      // Convert time from HH:MM:SS to HH:MM format if needed
      let timeValue = reminder.reminder_time || reminder.time || '';
      if (timeValue && timeValue.length > 5) {
        timeValue = timeValue.substring(0, 5); // Strip seconds (23:04:00 → 23:04)
      }

      this.reminderForm.patchValue({
        title: reminder.title,
        type: reminder.type,
        date: reminder.reminder_date || reminder.date,
        time: timeValue,
        priority: reminder.priority,
        alert_before: reminder.alert_before_minutes ?? 15,
        description: reminder.description || ''
      });

      // Handle location - could be string or array
      if (reminder.location) {
        if (Array.isArray(reminder.location)) {
          this.selectedLocationsFromDropdown = reminder.location;
        } else if (typeof reminder.location === 'string') {
          // Parse comma-separated string into multiple locations
          this.selectedLocationsFromDropdown = reminder.location
            .split(',')
            .map((loc) => ({ address: loc.trim() }))
            .filter((loc) => loc.address);
        }
      }
    }
    // Pre-fill from Task/Ticket data if provided
    else if (this.data?.entityName || this.data?.location) {
      this.reminderForm.patchValue({
        title: this.data.entityName || this.data.title || ''
      });

      // Handle location from Task/Ticket
      if (this.data.location) {
        if (typeof this.data.location === 'string') {
          // Parse comma-separated string or single location
          this.selectedLocationsFromDropdown = this.data.location
            .split(',')
            .map((loc) => ({ address: loc.trim() }))
            .filter((loc) => loc.address);
        } else {
          this.selectedLocationsFromDropdown = [{ address: this.data.location }];
        }
      }
    }
  }

  get selectedType(): ReminderType {
    return this.reminderForm.get('type')?.value || 'alert';
  }

  get selectedPriority(): ReminderPriority {
    return this.reminderForm.get('priority')?.value || 'medium';
  }

  selectType(type: ReminderType): void {
    this.reminderForm.patchValue({ type });
  }

  selectPriority(priority: ReminderPriority): void {
    this.reminderForm.patchValue({ priority });
  }

  onSubmit(): void {
    if (this.reminderForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    // Convert form data to backend format
    const formValue = this.reminderForm.value;

    // Convert locations array to comma-separated string
    const locationString = this.selectedLocationsFromDropdown
      .map((loc) => loc.address)
      .filter((address) => address)
      .join(', ');

    const formData: ReminderFormData = {
      title: formValue.title,
      description: formValue.description,
      reminder_date: formValue.date,  // Convert 'date' to 'reminder_date'
      reminder_time: formValue.time,  // Convert 'time' to 'reminder_time'
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,  // Detect browser timezone
      alert_before_minutes: formValue.alert_before ?? 15,
      type: formValue.type,
      priority: formValue.priority,
      location: locationString || undefined,
      is_private: true,  // Default to private for now
    };

    // Include reminderable data if provided (from Task/Ticket)
    if (this.data?.reminderable_type && this.data?.reminderable_id) {
      formData.reminderable_type = this.data.reminderable_type;
      formData.reminderable_id = this.data.reminderable_id;
    }

    // Call appropriate API based on mode
    const apiCall = this.isEditMode && this.editingReminderId
      ? this.remindersService.updateReminder(this.editingReminderId, formData)
      : this.remindersService.addReminder(formData);

    apiCall.subscribe({
      next: (reminder) => {
        this.isSubmitting = false;
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error.error?.message || `Failed to ${this.isEditMode ? 'update' : 'create'} reminder. Please try again.`;
      }
    });
  }

  getModalTitle(): string {
    if (this.isEditMode) {
      return 'Edit Reminder';
    }
    // Show entity name if provided (from Task/Ticket)
    if (this.data?.entityName) {
      return `Set Reminder for: ${this.data.entityName}`;
    }
    return 'Add Reminder';
  }

  getSubmitButtonText(): string {
    return this.isEditMode ? 'Update Reminder' : 'Add Reminder';
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  isFormValid(): boolean {
    return this.reminderForm.valid;
  }

  getLocationEnter(event: any): void {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.trim().length > 0) {
      this.backendApiService
        .getApi(`events/search/locations?search=${value}`)
        .subscribe((response: any) => {
          this.locationList = response?.data;
          setTimeout(() => {
            this.locationMenuTrigger.openMenu();
          });
        });
    }
  }

  onLocationSelected(item: any): void {
    if (!this.selectedLocationsFromDropdown.some((loc) => loc.id === item.id)) {
      this.selectedLocationsFromDropdown.push(item);
    }
    this.locationMenuTrigger.closeMenu();
    this.reminderForm.get('location')?.setValue('');
  }

  removeLocation(location: any): void {
    const index = this.selectedLocationsFromDropdown.indexOf(location);
    if (index >= 0) {
      this.selectedLocationsFromDropdown.splice(index, 1);
    }
  }

  onLocationSpanClick(): void {
    const inputValue = this.reminderForm.get('location')?.value?.trim();
    if (!inputValue) return;

    // Check for duplicates (by address or by name for custom)
    const alreadyExists = this.selectedLocationsFromDropdown.some(
      (loc) =>
        (loc.address &&
          loc.address.toLowerCase() === inputValue.toLowerCase()) ||
        (loc.name && loc.name.toLowerCase() === inputValue.toLowerCase())
    );
    if (!alreadyExists) {
      // Add as a custom location object (no id, but with address field)
      this.selectedLocationsFromDropdown.push({ address: inputValue });
    }
    this.locationMenuTrigger.closeMenu();
    // Clear the input in the form control
    this.reminderForm.get('location')?.setValue('');
  }
}
